/**
 * Daily Report
 * The report OmniMind writes each morning from every connected source, ranked
 * by what it means for a decision. Demo content - see src/demo/analytics.ts.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/shell/AppShell';
import { Card, Kpi, PageHead, Note, Table, Tag, Bar, DemoBadge } from '../components/ui';
import { ChartFrame, Trend, AreaTrend } from '../components/charts';
import { DAILY_REPORT as R } from '../demo/analytics';

const today = new Date().toLocaleDateString('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export const DailyReportPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <AppShell
      title="Daily Report"
      sub={`${today} · ${R.generatedAt}`}
      actions={
        <>
          <DemoBadge />
          <button className="btn btn-g" onClick={() => navigate('/reports')}>
            History
          </button>
          <button className="btn btn-g">Export PDF</button>
          <button className="btn btn-p">Regenerate</button>
        </>
      }
    >
      <PageHead
        eyebrow={`Generated ${R.generatedAt} · ${R.readTime} read · ${R.sources} sources`}
        title="What changed overnight"
        lede={R.headline}
      />

      <div className="g g5" style={{ marginBottom: 12 }}>
        {R.kpis.map((k) => (
          <Kpi
            key={k.label}
            label={k.label}
            value={k.value}
            unit={'unit' in k ? (k as any).unit : undefined}
            color={k.color}
            icon={k.icon as any}
            pip={'pip' in k ? (k as any).pip : undefined}
            dir={('dir' in k ? (k as any).dir : '') as any}
            sub={'sub' in k ? (k as any).sub : undefined}
            valueColor={k.color === 'var(--red)' ? 'var(--red)' : undefined}
          />
        ))}
      </div>

      <div className="g g3" style={{ marginBottom: 12 }}>
        <div className="s2">
          <Card title="The report" sub={`${R.sections.length} sections · ranked by decision impact`}>
            {R.sections.map((s) => (
              <div className="li" key={s.title}>
                <span
                  className={`tg tg-${s.tone}`}
                  style={{
                    height: 22,
                    borderRadius: 999,
                    fontSize: 10,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    flex: 'none',
                  }}
                >
                  {s.kind}
                </span>
                <div style={{ flex: 1 }}>
                  <h5>{s.title}</h5>
                  <p>{s.body}</p>
                  <div style={{ marginTop: 6, display: 'flex', gap: 5 }}>
                    <Tag>{s.evidence}</Tag>
                    <Tag tone="b">{s.metric}</Tag>
                  </div>
                </div>
                <div className="rt">
                  <button className="btn btn-g btn-s" onClick={() => navigate('/')}>
                    Ask →
                  </button>
                </div>
              </div>
            ))}
          </Card>
        </div>

        <Card title="Where this came from" sub={`${R.rowsRead.toLocaleString('en-US')} rows read`}>
          {R.contributing.map((c) => (
            <div className="prog-row" key={c.source}>
              <span className="l">{c.source}</span>
              <Bar pct={c.share} width="100%" />
              <span className="v">{c.rows.toLocaleString('en-US')}</span>
            </div>
          ))}
          <Note style={{ marginTop: 10 }}>
            Confidence <b>{R.confidence}%</b> — every figure above links to the record it came from.
            Nothing in this report is estimated.
          </Note>
        </Card>
      </div>

      <div className="g g2" style={{ marginBottom: 12 }}>
        <ChartFrame title="Revenue against plan" sub="weekly, $M">
          <AreaTrend
            data={R.revenueTrend}
            x="week"
            series={[
              { key: 'actual', label: 'Actual' },
              { key: 'plan', label: 'Plan' },
            ]}
          />
        </ChartFrame>

        <ChartFrame title="Gross margin" sub="weekly, % — GM-01">
          <Trend data={R.marginTrend} x="week" series={[{ key: 'margin', label: 'Gross margin' }]} unit="%" />
        </ChartFrame>
      </div>

      <Card title="Figures cited in this report" flush>
        <Table
          cols={[{ t: 'Section' }, { t: 'Metric' }, { t: 'Evidence' }, { t: 'Definition' }]}
          rows={R.sections.map((s) => [
            <b>{s.title}</b>,
            <Tag tone="b">{s.metric}</Tag>,
            <span className="dim">{s.evidence}</span>,
            <span className="dim">Certified · resolved before the query ran</span>,
          ])}
        />
      </Card>
    </AppShell>
  );
};
